import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatDate } from "../lib/format";

const ENTITY_TYPES = [
  { v: "", label: "Any" },
  { v: "estimate",             label: "Estimate" },
  { v: "estimate_day",         label: "Schedule day" },
  { v: "estimate_adjustment",  label: "Adjustment" },
  { v: "rate_card",            label: "Rate card" },
  { v: "policy",               label: "Policy / rule" },
  { v: "home",                 label: "Home" },
  { v: "funder",               label: "Funder" },
  { v: "person_supported",     label: "PWS" },
];
const ACTIONS = [
  { v: "", label: "Any" },
  { v: "create",   label: "Created" },
  { v: "update",   label: "Updated" },
  { v: "delete",   label: "Deleted" },
  { v: "submit",   label: "Submitted" },
  { v: "recall",   label: "Recalled" },
  { v: "approve",  label: "Approved" },
  { v: "reject",   label: "Rejected" },
  { v: "revert",   label: "Reverted" },
  { v: "adjust",   label: "Adjusted" },
];

export function Audit() {
  const [entityType, setEntityType] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openRow, setOpenRow] = useState<number | null>(null);

  const params: Record<string, string> = {};
  if (entityType) params.entity_type = entityType;
  if (actorEmail) params.actor_email = actorEmail;
  if (action) params.action = action;
  if (from) params.from = from;
  if (to) params.to = to;

  const r = useQuery({ queryKey: ["audit", params], queryFn: () => api.auditList(params) });
  const rows: any[] = r.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Audit log</h1>
        <p className="text-slate-500 mt-1">Every change made in the tool. Click a row to see what was different before and after.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
        <label className="block">
          <div className="text-xs text-slate-500 mb-1">What</div>
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded">
            {ENTITY_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-xs text-slate-500 mb-1">Action</div>
          <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded">
            {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
          </select>
        </label>
        <label className="block col-span-2 sm:col-span-1">
          <div className="text-xs text-slate-500 mb-1">Who (email contains)</div>
          <input value={actorEmail} onChange={(e) => setActorEmail(e.target.value)} placeholder="e.g. james.smith"
                 className="w-full px-3 py-2 border border-slate-200 rounded" />
        </label>
        <label className="block">
          <div className="text-xs text-slate-500 mb-1">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded" />
        </label>
        <label className="block">
          <div className="text-xs text-slate-500 mb-1">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded" />
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {r.isLoading && <div className="p-6 text-slate-500 text-sm">Loading...</div>}
        {rows.length === 0 && !r.isLoading && <div className="p-10 text-center text-slate-500 text-sm">No matching entries.</div>}
        {rows.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => <AuditRow key={row.id} row={row} open={openRow === row.id} onToggle={() => setOpenRow((cur) => cur === row.id ? null : row.id)} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

function AuditRow({ row, open, onToggle }: { row: any; open: boolean; onToggle: () => void }) {
  const before = useMemo(() => parseJson(row.before_json), [row.before_json]);
  const after  = useMemo(() => parseJson(row.after_json),  [row.after_json]);
  const summary = humanSummary(row, before, after);
  const diff = useMemo(() => computeDiff(before, after), [before, after]);
  const date = new Date(row.acted_at);

  return (
    <li>
      <button onClick={onToggle} className="w-full text-left px-4 sm:px-6 py-3 hover:bg-slate-50 flex items-start gap-3">
        <ActionPill action={row.action} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-800">{summary}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {row.actor_email ?? "system"}
            {row.reason && <span> · "{row.reason}"</span>}
          </div>
        </div>
        <div className="text-xs text-slate-400 whitespace-nowrap shrink-0">
          {date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      </button>
      {open && (
        <div className="px-4 sm:px-6 pb-5 pt-1 bg-slate-50/50">
          {diff.length === 0 ? (
            <div className="text-xs text-slate-500 italic px-2">No field-level changes (likely a status-only action).</div>
          ) : (
            <table className="w-full text-xs bg-white rounded-lg border border-slate-200 overflow-hidden">
              <thead className="bg-slate-50 text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-3 py-2 w-1/3">Field</th>
                  <th className="text-left px-3 py-2 w-1/3">Before</th>
                  <th className="text-left px-3 py-2 w-1/3">After</th>
                </tr>
              </thead>
              <tbody>
                {diff.map((d) => (
                  <tr key={d.field} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-700">{prettyField(d.field)}</td>
                    <td className="px-3 py-2 text-slate-500"><DiffValue field={d.field} value={d.before} kind="before" /></td>
                    <td className="px-3 py-2 text-slate-800"><DiffValue field={d.field} value={d.after} kind="after" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {row.entity_type === "estimate" && (
            <div className="mt-3 text-xs">
              <Link to={`/approvals/${row.entity_id}`} className="text-liaise-blue hover:underline">Open this estimate →</Link>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function ActionPill({ action }: { action: string }) {
  const map: Record<string, string> = {
    create:  "bg-liaise-green/15 text-emerald-800",
    update:  "bg-liaise-blue/15 text-liaise-navy",
    delete:  "bg-liaise-red/15 text-red-700",
    submit:  "bg-liaise-yellow/30 text-amber-900",
    recall:  "bg-slate-100 text-slate-700",
    approve: "bg-liaise-green/15 text-emerald-800",
    reject:  "bg-liaise-red/15 text-red-700",
    revert:  "bg-liaise-orange/15 text-orange-800",
    adjust:  "bg-liaise-orange/15 text-orange-800",
  };
  const labels: Record<string, string> = {
    create: "Created", update: "Updated", delete: "Deleted", submit: "Submitted",
    recall: "Recalled", approve: "Approved", reject: "Rejected", revert: "Reverted", adjust: "Adjusted",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${map[action] ?? "bg-slate-100 text-slate-700"}`}>
      {labels[action] ?? action}
    </span>
  );
}

function DiffValue({ field, value, kind }: { field: string; value: any; kind: "before" | "after" }) {
  if (value === null || value === undefined || value === "") return <span className="text-slate-400 italic">—</span>;
  if (isMoneyField(field) && typeof value === "number") {
    return <span className={`tabular-nums ${kind === "before" ? "line-through opacity-70" : ""}`}>{formatMoney(value / (isPenceField(field) ? 100 : 1))}</span>;
  }
  if (isDateField(field) && typeof value === "string") {
    return <span className={kind === "before" ? "line-through opacity-70" : ""}>{formatDate(value)}</span>;
  }
  if (typeof value === "boolean") return <span className={kind === "before" ? "line-through opacity-70" : ""}>{value ? "Yes" : "No"}</span>;
  if (typeof value === "object") return <span className="text-slate-500">[{Array.isArray(value) ? `${value.length} item${value.length === 1 ? "" : "s"}` : "object"}]</span>;
  return <span className={kind === "before" ? "line-through opacity-70" : ""}>{String(value)}</span>;
}

function isDateField(f: string) {
  return f === "start_date" || f === "end_date" || f.endsWith("_at") || f.endsWith("_date");
}

function humanSummary(row: any, before: any, after: any): string {
  const a = row.action;
  const t = row.entity_type;
  const id = (row.entity_id ?? "").slice(0, 8);
  if (t === "estimate") {
    if (a === "create")  return `Created a new estimate`;
    if (a === "submit")  return `Submitted estimate for approval`;
    if (a === "approve") return `Approved the estimate`;
    if (a === "reject")  return `Rejected the estimate`;
    if (a === "recall")  return `Recalled the submission`;
    if (a === "revert")  return `Reverted the estimate status`;
    if (a === "delete")  return `Deleted the draft`;
    if (a === "update") {
      const fields = Object.keys(diffShallow(before ?? {}, after ?? {}));
      if (!fields.length) return `Edited the estimate`;
      return `Edited ${fields.length === 1 ? prettyField(fields[0]) : `${fields.length} fields`}`;
    }
    return `${a} estimate ${id}`;
  }
  if (t === "estimate_adjustment") {
    if (a === "create") {
      const amt = after?.amount_pence ?? 0;
      const reason = after?.reason ?? "";
      const sign = amt >= 0 ? "+" : "-";
      return `Added adjustment ${sign}${formatMoney(Math.abs(amt) / 100)}${reason ? ` "${reason}"` : ""}`;
    }
    if (a === "delete") {
      const amt = before?.amount_pence ?? 0;
      const reason = before?.reason ?? "";
      const sign = amt >= 0 ? "+" : "-";
      return `Removed adjustment ${sign}${formatMoney(Math.abs(amt) / 100)}${reason ? ` "${reason}"` : ""}`;
    }
    if (a === "update") {
      const beforeAmt = (before?.amount_pence ?? 0) / 100;
      const afterAmt  = (after?.amount_pence  ?? 0) / 100;
      if (beforeAmt !== afterAmt) return `Adjusted ${formatMoney(beforeAmt)} → ${formatMoney(afterAmt)}`;
      return `Updated adjustment reason`;
    }
    return `${a} adjustment`;
  }
  if (t === "estimate_day") return `Edited the day schedule`;
  if (t === "rate_card") {
    const pence = after?.pence ?? before?.pence;
    return `${a === "create" ? "Set" : a === "update" ? "Updated" : "Changed"} rate card${pence ? ` to ${formatMoney(pence / 100)}` : ""}`;
  }
  if (t === "policy") {
    const key = row.entity_id;
    return `${a === "create" ? "Set" : "Changed"} rule "${prettyField(key)}"`;
  }
  if (t === "home")             return `${a} home ${id}`;
  if (t === "funder")           return `${a} funder ${id}`;
  if (t === "person_supported") return `${a} PWS ${id}`;
  return `${a} ${t} ${id}`;
}

interface DiffEntry { field: string; before: any; after: any }
function computeDiff(before: any, after: any): DiffEntry[] {
  if (!before && !after) return [];
  if (!before) before = {};
  if (!after)  after  = {};
  // For arrays/days, skip — we summarise in the row line instead.
  if (Array.isArray(before) || Array.isArray(after)) return [];

  const beforeIsEmpty = !before || Object.keys(before).length === 0;
  const afterIsEmpty  = !after  || Object.keys(after).length  === 0;

  // For delete actions (after is empty), show what was there
  // For create actions (before is empty), show what was set
  // For updates: only show fields where the value actually changed AND the field is present in the payload (so partial-patch updates don't list every "missing" field)
  const out: DiffEntry[] = [];
  if (beforeIsEmpty) {
    for (const k of Object.keys(after)) {
      if (SKIP_FIELDS.has(k)) continue;
      if (after[k] === null || after[k] === undefined || after[k] === "") continue;
      out.push({ field: k, before: undefined, after: after[k] });
    }
  } else if (afterIsEmpty) {
    for (const k of Object.keys(before)) {
      if (SKIP_FIELDS.has(k)) continue;
      if (before[k] === null || before[k] === undefined || before[k] === "") continue;
      out.push({ field: k, before: before[k], after: undefined });
    }
  } else {
    // True update: only fields that are in BOTH and differ
    for (const k of Object.keys(after)) {
      if (SKIP_FIELDS.has(k)) continue;
      const b = before[k];
      const a = after[k];
      if (JSON.stringify(b) !== JSON.stringify(a)) {
        out.push({ field: k, before: b, after: a });
      }
    }
  }
  return out.sort((x, y) => prettyField(x.field).localeCompare(prettyField(y.field)));
}
function diffShallow(b: any, a: any): Record<string, [any, any]> {
  if (!b || Object.keys(b).length === 0) return Object.fromEntries(Object.entries(a ?? {}).filter(([k, v]) => !SKIP_FIELDS.has(k) && v != null && v !== "").map(([k, v]) => [k, [undefined, v]]));
  if (!a || Object.keys(a).length === 0) return Object.fromEntries(Object.entries(b ?? {}).filter(([k, v]) => !SKIP_FIELDS.has(k) && v != null && v !== "").map(([k, v]) => [k, [v, undefined]]));
  const out: Record<string, [any, any]> = {};
  for (const k of Object.keys(a)) {
    if (SKIP_FIELDS.has(k)) continue;
    if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) out[k] = [b[k], a[k]];
  }
  return out;
}

const SKIP_FIELDS = new Set([
  "id", "updated_at", "created_at", "version", "estimate_id",
  "approver_override_set_at", "admin_override_set_at",
  // internal/system fields — noisy and never user-meaningful
  "approver_entra_oid", "claimant_entra_oid",
  "approver_override_set_by_email", "admin_override_set_by_email",
  "snapshot_pws_annual_contribution", "snapshot_pws_partnership_rag", "snapshot_funder_case_by_case",
  "submitted_at", "approved_at", "pdf_blob_url",
  "created_by_oid", "created_by_email",
]);
const FIELD_LABELS: Record<string, string> = {
  pws_initials: "PWS initials",
  pws_id: "PWS",
  home_id: "Home",
  funder_id: "Funder",
  start_date: "Start date",
  end_date: "End date",
  staff_count: "Staff",
  total_amount: "Total",
  job_title: "Job title",
  status: "Status",
  status_reason: "Reason",
  amount_pence: "Amount",
  reason: "Reason",
  rate_day_pence: "Day rate",
  rate_night_pence: "Night rate",
  rate_sleep_in_pence: "Sleep-in rate",
  rate_expense_per_day_pence: "Staff expenses rate",
  finance_cc_email: "Finance CC email",
  above_threshold_pounds: "Above-threshold warning (£)",
  sanity_green_pct: "Sanity green band (%)",
  sanity_amber_pct: "Sanity amber band (%)",
  flag_rag_red: "Flag RAG = Red",
  flag_case_by_case: "Flag Case-by-Case",
  quote_validity_days: "Quote validity (days)",
  notification_sender_email: "Notification sender",
  suggested_acuity_low: "Suggested schedule — Low acuity",
  suggested_acuity_medium: "Suggested schedule — Medium acuity",
  suggested_acuity_high: "Suggested schedule — High acuity",
  suggested_acuity_very_high: "Suggested schedule — Very High acuity",
};
function prettyField(f: string) {
  if (FIELD_LABELS[f]) return FIELD_LABELS[f];
  return f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function isMoneyField(f: string) {
  return f === "total_amount" || f === "amount_pence" || f.endsWith("_pence") || f === "pence";
}
function isPenceField(f: string) {
  return f === "amount_pence" || f.endsWith("_pence") || f === "pence";
}
function parseJson(s: string | null | undefined): any | null {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}
